import { openDB, type IDBPDatabase } from 'idb';
import type { QueueEntry } from './types';

/**
 * Stockage de la file. IndexedDB et non localStorage : la file doit survivre à la fermeture
 * de l'onglet, tenir plusieurs kilo-octets de commentaires, et rester lisible par le service
 * worker — trois choses que localStorage ne fait pas.
 */
const DB_NAME = 'cercle-des-anges';
const DB_VERSION = 1;
const STORE = 'outbox';

let dbPromise: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE)) {
        database.createObjectStore(STORE, { keyPath: 'id' });
      }
    },
  });
  return dbPromise;
}

export interface QueueStorage {
  all(): Promise<QueueEntry[]>;
  get(id: string): Promise<QueueEntry | undefined>;
  put(entry: QueueEntry): Promise<void>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
}

export const indexedDbStorage: QueueStorage = {
  async all(): Promise<QueueEntry[]> {
    const entries = (await (await db()).getAll(STORE)) as QueueEntry[];
    // Ordre de saisie : rejouer les notes dans le désordre donnerait des résultats
    // différents si deux entrées visaient la même clé.
    return entries.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
  },

  async get(id: string): Promise<QueueEntry | undefined> {
    return (await (await db()).get(STORE, id)) as QueueEntry | undefined;
  },

  async put(entry: QueueEntry): Promise<void> {
    await (await db()).put(STORE, entry);
  },

  async remove(id: string): Promise<void> {
    await (await db()).delete(STORE, id);
  },

  async clear(): Promise<void> {
    await (await db()).clear(STORE);
  },
};

/** Implémentation en mémoire, pour les tests et le rendu serveur éventuel. */
export function createMemoryStorage(): QueueStorage {
  const entries = new Map<string, QueueEntry>();
  return {
    all: () =>
      Promise.resolve(
        [...entries.values()].sort((a, b) => a.queuedAt.localeCompare(b.queuedAt)),
      ),
    get: (id) => Promise.resolve(entries.get(id)),
    put: (entry) => {
      entries.set(entry.id, entry);
      return Promise.resolve();
    },
    remove: (id) => {
      entries.delete(id);
      return Promise.resolve();
    },
    clear: () => {
      entries.clear();
      return Promise.resolve();
    },
  };
}
